import React, { useEffect, useState } from 'react'
import {
  Edit,
  ImageField,
  ImageInput,
  required,
  SimpleForm,
  TextInput,
  useEditController,
  useNotify,
  useRedirect,
  useTranslate,
  useUpdate
} from 'react-admin'
import { CardCommon } from 'src/components/uiElements'
import { EditInputsWrap } from 'src/components/uiElements/CRUD/EditInputsWrap'
import { CardCrudBottomButtons } from 'src/components/uiElements/CRUD/CardCrudBottomButtons'
import { Grid, Skeleton } from '@mui/material'
import { DateTimePickers } from 'src/components/uiElements/react-hook-fields'
import { bannerTypeServices } from 'src/services/reservation/bannerType/BannerType.service'
import { SelectInput } from 'src/components/uiElements/fields/SelectInput'
import { productTypeServices } from 'src/services/reservation/product-type/product_type.service'
import { NumberInput } from 'src/components/uiElements/fields/NumberInput'
import { bannerServices } from 'src/services/reservation/banner/Banner.service'
import { currencyServices } from 'src/services/reservation/currency/currency.service'
import { compressImage } from 'src/helper/CompressImage'
import { generateRandomString } from 'src/helper/generateRandomString'

export const BannerEdit = (props, { id }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()
  const [update] = useUpdate()
  const [bannerTypeList, setBannerTypeList] = useState([])
  const [loadingBannerTypeList, setLoadingBannerTypeList] = useState(false)
  const [productTypeList, setProductTypeList] = useState([])
  const [loadingProductTypeList, setLoadingProductTypeList] = useState(false)
  const [currencyList, setCurrencyList] = useState([])
  const [loadingCurrencyList, setLoadingCurrencyList] = useState(false)
  const { record } = useEditController({ resource: 'banner', id })

  useEffect(() => {
    ;(async () => {
      await Promise.all([
        getBannerTypes(),
        getProductTypes(),
        getCurrencies()
      ])
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getBannerTypes = async () => {
    try {
      setLoadingBannerTypeList(true)
      const allBannerTypes = await bannerTypeServices.getByAll({ limit: 999 })
      setBannerTypeList(allBannerTypes)
      setLoadingBannerTypeList(false)
    } catch (error) {
      setBannerTypeList([])
      setLoadingBannerTypeList(false)
    }
  }

  const getProductTypes = async () => {
    try {
      setLoadingProductTypeList(true)
      const allProducts = await productTypeServices.getByParam()
      setProductTypeList(allProducts)
      setLoadingProductTypeList(false)
    } catch (error) {
      setProductTypeList([])
      setLoadingProductTypeList(false)
    }
  }

  const getCurrencies = async () => {
    try {
      setLoadingCurrencyList(true)
      const allCurrencies = await currencyServices.getByAll()
      setCurrencyList(allCurrencies)
      setLoadingCurrencyList(false)
    } catch (error) {
      setCurrencyList([])
      setLoadingCurrencyList(false)
    }
  }

  const onSuccess = () => {
    notify('ra.notification.updated_success_masculine', {
      type: 'success',
      messageArgs: { element: translate('ra.single_entity.banner') }
    })
    redirect('/banner')
  }

  const onError = () => {
    notify('ra.notification.updated_error_masculine', {
      type: 'error',
      messageArgs: { element: translate('ra.single_entity.banner') }
    })
  }

  const uploadFilesServices = async (image, imageMobile) => {
    if (!image.rawFile && !imageMobile.rawFile) {
      return {};
    }
    
    const formData = new FormData();
    if (image.rawFile) {
      const fieldName = 'image_url'
      let imageToUpload = image.rawFile
      if (imageToUpload.size >= 1000000) {
        const compressImageFileBlob = await compressImage(image.rawFile, fieldName)
        if (compressImageFileBlob){
          const randomSting = generateRandomString(9)
          const compressedFile = new File([compressImageFileBlob], `${randomSting}_${image.rawFile.name}`, {
            type: compressImageFileBlob.type,
            lastModified: imageMobile?.rawFile?.lastModified || new Date() 
          });
          imageToUpload = compressedFile
        } else {
          imageToUpload = null
        }
      }

      formData.append(fieldName, imageToUpload);
    }
    if (imageMobile.rawFile) {
      const fieldNameMobile = 'image_url_mobile'
      let imageMobileToUpload = imageMobile.rawFile
      if (imageMobileToUpload.size >= 1000000) {
        const compressImageMobileBlob = await compressImage(image.rawFile, fieldNameMobile)
        if (compressImageMobileBlob){
          const randomSting = generateRandomString(9)
          const compressedFileMobile = new File([compressImageMobileBlob], `${randomSting}_${image.rawFile.name}`, {
            type: compressImageMobileBlob.type,
            lastModified: imageMobile?.rawFile?.lastModified || new Date() 
          });
          imageMobileToUpload = compressedFileMobile
        } else {
          imageMobileToUpload = null
        }
      }

      formData.append(fieldNameMobile, imageMobileToUpload);
    }
    try {
      const response = await bannerServices.uploadImages(formData);
      return response;
    } catch (error) {
      console.error('Error uploading images', error);
      return null;
    }
  };

  const handleSubmit = async (data) => {
    const response = await uploadFilesServices(data.image_url, data.image_url_mobile)
    if (response) {
      await update(
        'banner',
        {
          id: record.id,
          data: {
            ...data,
            ...response
          },
          previousData: record
        },
        { mutationMode: 'pessimistic', onSuccess, onError }
      )
    } else {
      onError()
    }
  }

  return (
    <Edit {...props} mutationMode='pessimistic' mutationOptions={{ onSuccess, onError }} actions={null}>
      <CardCommon title={translate('ra.title.update_banner')}>
        <SimpleForm
          toolbar={<CardCrudBottomButtons btnSave={true} btnDelete={true} fieldsName={['short_name']} genderElement='masculine' />}
          onSubmit={handleSubmit}
        >
          <Grid container spacing={3}>
            <EditInputsWrap label={translate('ra.field.id')}>
              <TextInput disabled source='id' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.short_name')} required>
              <TextInput source='short_name' label={false} fullWidth validate={[required()]} />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.title')}>
              <TextInput source='title' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.description')}>
              <TextInput source='description' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.datetime_from')}>
              <DateTimePickers source='datetime_from' />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.datetime_to')}>
              <DateTimePickers source='datetime_to' />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.banner_type')} required>
              {loadingBannerTypeList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  size='small'
                  optionsText={['long_name']}
                  fullWidth
                  label={false}
                  source='banner_type_id'
                  defaultValue={record?.banner_type_id}
                  options={bannerTypeList}
                  validate={[required()]}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.product_type')}>
              {loadingProductTypeList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  size='small'
                  optionsText={['long_name']}
                  fullWidth
                  label={false}
                  source='product_type_id'
                  defaultValue={record?.product_type_id}
                  options={productTypeList}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.flow')} required>
              <TextInput source='flow' label={false} fullWidth validate={[required()]} />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.site_type')}>
              <TextInput source='site_type' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.group')}>
              <TextInput source='group' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.order')}>
              <NumberInput source='order' label={false} noDecimal />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.button_text')}>
              <TextInput source='button_text' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.link')}>
              <TextInput source='link' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.label')}>
              <TextInput source='label' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.category')}>
              <NumberInput source='category' label={false} noDecimal />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.caption')}>
              <TextInput source='caption' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.currency')}>
              {loadingCurrencyList ? (
                <Skeleton variant='rectangular' width={'100%'} height={40} />
              ) : (
                <SelectInput
                  size='small'
                  optionsText={['long_name']}
                  fullWidth
                  label={false}
                  source='currency_id'
                  defaultValue={record?.currency_id}
                  options={currencyList}
                />
              )}
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.amount')}>
              <NumberInput source='amount' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.html_content')}>
              <TextInput source='html_content' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.html_content_additional')}>
              <TextInput source='html_content_additional' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.meta')}>
              <TextInput source='meta' label={false} fullWidth />
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.image')} required>
              <ImageInput source='image_url' label={''} className='image-input' validate={[required()]}>
                <ImageField source='src' title='title' />
              </ImageInput>
            </EditInputsWrap>
            <EditInputsWrap label={translate('ra.field.mobile_image')} required>
              <ImageInput source='image_url_mobile' label={''} className='image-input' validate={[required()]}>
                <ImageField source='src' title='title' />
              </ImageInput>
            </EditInputsWrap>
          </Grid>
        </SimpleForm>
      </CardCommon>
    </Edit>
  )
}